import {uuid}          from "@supabase/supabase-js/dist/main/lib/helpers"
import {doGet, doPost} from "./do-fetch"
import moment          from "moment"
import {scorePrepKit}  from "./mini"
import {fetchBuilderContent} from "./builder"

/*
 * Quick-Start question actions. These carry only the user-facing text and a type — the AI
 * instructions for each live in the `config` table (QUESTION_ACTION_*_PROMPT) so they can be
 * edited at runtime from the admin App Config screen; the API resolves them from the type sent
 * with the message (see 062 / schema-changes/005).
 */
const HINT = {
	type: "HINT",
	label: "Hint, please",
	displayText: "Give me a hint"
}

const EXAMPLE = {
	type: "EXAMPLE",
	label: "Real life example",
	displayText: "Give me a real-world example"
}

const MEMORIZE = {
	type: "MEMORIZE",
	label: "How to remember this",
	displayText: "How can I remember this?"
}

const WHY_WRONG = {
	type: "WHY_WRONG",
	label: "Why was my answer wrong?",
	displayText: "Why was my answer wrong?"
}

export const PREPKIT_QUESTION_ACTIONS = [HINT, EXAMPLE]

/*
 * Which Quick-Start buttons to show for the current answer state:
 *   unanswered          → hint / example
 *   answered correctly  → how to remember this / example
 *   answered wrong      → why was my answer wrong / example
 */
export const getQuestionActions = ({answered, isCorrect}) => {
	if (!answered) {
		return [HINT, EXAMPLE]
	}

	return isCorrect
		? [MEMORIZE, EXAMPLE]
		: [WHY_WRONG, EXAMPLE]
}

/*
 * True while a reply is in flight: the AI message streams (the flag is cleared in the stream's
 * onComplete) or the variant placeholder is up. The queued user message and the streaming AI
 * message are appended together, so this also covers "just sent".
 */
export const hasStreamingMessage = (messages) => Boolean(messages && messages.some(m =>
	m.streaming === true || m.message_sub_type === 'QUESTION_VARIANT_LOADING'
))

/*
 * Last question the user was on, per prepkit — browser-local only, nothing server-side.
 * localStorage can throw (private browsing, disabled storage), and remembering where someone
 * was is never worth breaking navigation over, so both helpers swallow failures.
 */
const lastQuestionKey = (prepKitId) => `last-question-${prepKitId}`

export const rememberLastQuestion = (prepKitId, code) => {
	if (!prepKitId || !code) return
	try {
		localStorage.setItem(lastQuestionKey(prepKitId), code)
	}
	catch (e) {
		// ignore — storage unavailable
	}
}

export const getLastQuestion = (prepKitId) => {
	if (!prepKitId) return null
	try {
		return localStorage.getItem(lastQuestionKey(prepKitId))
	}
	catch (e) {
		return null
	}
}

const fetchPrepKit = async (preKitId, includePackage) => {
	if (preKitId) {
		// only fetch the prepKit data if not loaded
		const query = includePackage ? '?includeLicenseAndPackage=true' : ''
		const response = await doGet(`/mini/prepkit/${preKitId}${query}`)

		if (response.ok) {
			const data = await response.json()
			scorePrepKit(data) // important to do this before you set local state

			return data
		}
		else if (response.status === 404) {
			throw "PrepKit not found."
		}
	}
}

const fetchTryPackageUrl = async (packageToTry, questionCode) => {
	const packageToken = packageToTry.token || packageToTry
	const response = await doGet(`/mini/package/${packageToken}/try`)

	if (response.ok) {
		const data = await response.json()
		const prepKit = data.prepKit
		const firstQuestion = data.firstQuestion
		if(prepKit) {
			if(!questionCode && firstQuestion) {
				questionCode = firstQuestion
			}
			return `/prepkit/${prepKit.id}` + (questionCode ? `/q/${questionCode}` : '')
		}
		else {
			throw `Your complimentary study session has expired.`
		}
	}
	else if (response.status === 403) {
		// forbidden
		throw `Your complimentary study session has expired.`
	}
}

const fetchPackageTrialAvailable = async (packageToTry, questionCode) => {
	const packageToken = packageToTry.token || packageToTry
	const response = await doGet(`/mini/package/${packageToken}/trial-available`)
	return await response.json()
}

const fetchPackageContent = async (packageData) => {
	const entryName = `package-page-${packageData.token}`
	const defaultName = 'package-page-default'
	return fetchBuilderContent('package-landing-page', entryName, defaultName, (content, key, value, variableName) => {
		if(variableName === 'name') {
			return packageData.name
		}
	})
}

const createQueuedUserMessage = (body, animate, type, messageSubType) => {
	return {
		id: uuid(), // it is important that the client creates the id
		body: body,
		message_type: "TEXT",
		message_sub_type: messageSubType,
		author_type: "USER",
		animate: true,
		type: false,
		created_at: new Date()
	}
}

const createQueuedAiMessage = (body, animate, type, messageSubType) => {
	return {
		id: uuid(), // it is important that the client creates the id
		body: body,
		message_type: "TEXT",
		message_sub_type: messageSubType,
		author_type: "AI",
		animate: animate === true,
		streaming: true,
		type: type === true,
		created_at: new Date()
	}
}

const createStreamingAiMessage = (animate, type, messageSubType) => {
	return {
		id: uuid(), // it is important that the client creates the id
		body: "",
		message_type: "TEXT",
		message_sub_type: messageSubType,
		author_type: "AI",
		streaming: true,
		animate: animate === true,
		type: type === true,
		created_at: new Date()
	}
}

const fmtPath = (path, removeLast, removeFirstDelim, newDelim) => {
	const parts = path ? path.split('/') : []
	removeLast && parts.pop()
	removeFirstDelim && parts.shift()
	return parts.join(newDelim || ' / ')
}

const fmtModels = (models) => {
	const parts = models ? models.split(',') : []
	const unique =	[...new Set(parts)]
	return unique.join(', ')
}

const fmtDttm = (dttm) => {
	return dttm && moment(dttm).format('MMM Do h:mm:ssa')
}

const fmtDt = (dt) => {
	return dt && moment(dt).format('M/D/YYYY')
}

/*
 * Date AND time, keeping the year -- for a value where both halves matter, such as a licence or
 * PrepKit expiry, which can be a year out and which an admin needs to read to the minute.
 *
 * Not fmtDttm above: that one is 'MMM Do h:mm:ssa', which carries seconds but NO year, so it reads
 * as a log timestamp rather than a date. Using it for an expiry would trade the year for seconds
 * nobody needs. Local time, like every other formatter here.
 */
const fmtDtAndTime = (dttm) => {
	return dttm && moment(dttm).format('M/D/YYYY h:mma')
}

const fmtCal = (dttm) => {
	return dttm && moment(dttm).calendar()
}

const fmtDur = (start, end) => {
	return start && end && (((moment(end).valueOf() - moment(start).valueOf()) / 1000) + 's')
}

const fmt2Dec = (number) => {
	return (Math.round(number * 100) / 100).toFixed(2);
}

const fmtPct = (number) => {
	return Math.round(number * 100) + '%'
}

const fmtMinSec = (millis) => {
	const totalSeconds = Math.floor(millis / 1000)
	const hours = Math.floor(totalSeconds / 3600)
	const minutes = Math.floor((totalSeconds % 3600) / 60)
	const seconds = totalSeconds % 60

	const formattedMinutes = minutes
	const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds

	if (hours > 0) {
		return `${hours}h ${formattedMinutes}m ${formattedSeconds}s`
	} else {
		return `${formattedMinutes}m ${formattedSeconds}s`
	}
}

const shortenString = (str, maxLen) => {
	if(!str) return str
	maxLen = maxLen || 400
	if (str.length <= maxLen) return str

	const start = str.substring(0, maxLen / 2 - 1)
	const end = str.substring(str.length - maxLen / 2 + 1)

	return `${start}...${end}`
}

const typeIt = (s, typingSpeed, onType, onComplete) => {
	typingSpeed = !typingSpeed ? 300 : typingSpeed
	const words = s.split(" ")
	const typingInterval = setInterval(() => {
		if (words.length > 0) {
			onType(" " + words.shift())
		}
		else {
			clearInterval(typingInterval)
			if (onComplete) {
				onComplete()
			}
		}
	}, typingSpeed)
	return typingInterval
}

const isUserMessage = (message) => {
	return message &&
		message.author_type === "USER"
}

const isAiMessage = (message) => {
	return message &&
		message.author_type === "AI"
}

const isExpired = (prepKit) => {
	if(!prepKit) return false
	const expiresIn = getExpiresIn(prepKit)
	return Boolean(expiresIn <= 0)
}

const getExpiresIn = (prepKit) => {
	if(!prepKit) return -1
	const now = new Date().getTime()
	const then = new Date(prepKit.expires_at).getTime()
	const expiresIn = then - now
	//console.log({now, then, expiresIn})
	return expiresIn
}

/**
 *
 * @param productOrTopic
 * @returns {null|{questions}|*}
 */
const findFirstTopicWithQuestions = (productOrTopic) => {
	if (productOrTopic.questions && productOrTopic.questions.length) {
		return productOrTopic
	}
	else if (productOrTopic.topics && productOrTopic.topics.length) {
		for (const topic of productOrTopic.topics) {
			const result = findFirstTopicWithQuestions(topic)
			if (result) {
				return result
			}
		}
	}
	return null
}

/**
 *
 * @param productOrTopic
 * @returns {{is_leaf}|*|null|any}
 */
const findFirstLeafTopic = (productOrTopic) => {
	if(productOrTopic.is_leaf) {
		return productOrTopic
	}
	else if (productOrTopic.topics && productOrTopic.topics.length) {
		for (const topic of productOrTopic.topics) {
			if(topic.is_leaf) {
				return topic
			}
		}
	}
	return null
}

/**
 *
 * @param productOrTopic
 * @param topicId
 * @param questionId
 * @returns {null|{question: (*), topic: (any|*)}}
 */
const findTopicAndQuestion = (productOrTopic, topicId, questionId) => {

	const topic = findTopicById(productOrTopic, topicId)

	if(topic) {
		const question = findQuestionInTopic(topic, questionId)
		return {topic, question}
	}
	else {
		return null
	}
}

/**
 *
 * @param parent
 * @param topicId
 * @returns {null|any|*}
 */
const findTopicById = (parent, topicId, depth) => {
	depth = depth || 0
	console.log(`findTopicById(${parent?.id},${topicId}, ${depth})`)
	if(parent.topics && parent.topics.length > 0) {
		for (const topic of parent.topics) {
			if(topic.id == topicId) {
				console.log(`MATCH FOUND: findTopicById(${parent?.id},${topicId}) ${topic.id} == ${topicId}`)
				return topic
			}
			else {
				console.log(`MATCH NOT FOUND: recursing`)
				const result = findTopicById(topic, topicId, depth+1)
				if(result) {
					return result
				}
			}
		}
	}
	return null
}

/**
 *
 * @param topic
 * @param questionId
 * @returns {null|*}
 */
const findQuestionInTopic = (topic, questionId) => {
	console.log(`Entering findQuestionInTopic with topic ID: ${topic.id} and questionId: ${questionId}`);

	if (topic.questions && topic.questions.length > 0) {
		console.log('Searching in questions of the current topic.')
		for (const question of topic.questions) {
			if (question.id == questionId) {
				console.log(`Found question with ID: ${questionId}`)
				return question;
			}
			else {
				console.log(`Comparing question failed: ${question.id} === ${questionId}`);
			}
		}
	} else if (topic.topics && topic.topics.length > 0) {
		console.log('Entering child topics.');
		for (const child of topic.topics) {
			const question = findQuestionInTopic(child, questionId);
			if (question) {
				console.log(`Found question with ID: ${questionId} in child topic.`);
				return question;
			}
		}
	}
	console.log(`Exiting findQuestionInTopic; Did not find question with ID: ${questionId}`);
	return null;
};

/**
 * Returns an prepKit along with topics and product, etc.
 * @param id
 * @returns {Promise<*>}
 */
const findPrepKit = async (id) => {
	const url = `/prepkit/${id}`;
	const response = doGet(url);
	return response.json();
}

/**
 *
 * @param prepKit
 */
const isPrepKitExpired = (license) => {
	const expired = moment(license.expires_at).isBefore(moment())
	return expired
}

/**
 * Returns letter for corresponding answer index.  0=A, 1=B, 2=C, 3=D
 *
 * @param optionIndex
 * @returns {string}
 */
const OPTION_LETTERS = ['A','B','C','D']
const ABCD = "ABCD"
const abcd = (optionIndex) => {
	return ABCD.charAt(optionIndex)
}

/**
 * Returns the index for corresponding answer letter.  A=0, B=1, C=2, D=3
 *
 * @param optionIndex
 * @returns {string}
 */
const dcba = (optionLetter) => {
	console.log(`dcba(${optionLetter})`)
	let val = "ABCD".indexOf(optionLetter?.toUpperCase())
	if(val === -1) {
		if(Number.isInteger(optionLetter)) {
			return optionLetter
		}
	}
	return val
}

export {
	fetchPrepKit,
	abcd,
	dcba,
	createQueuedUserMessage,
	createQueuedAiMessage,
	createStreamingAiMessage,
	isUserMessage,
	isAiMessage,
	typeIt,
	fmtPath,
	fmtModels,
	fmtDttm,
	fmtDt,
	fmtDtAndTime,
	fmtDur,
	fmt2Dec,
	fmtMinSec,
	findFirstTopicWithQuestions,
	findFirstLeafTopic,
	findQuestionInTopic,
	findTopicAndQuestion,
	fmtCal,
	fmtPct,
	shortenString,
	isExpired,
	getExpiresIn,
	fetchTryPackageUrl,
	fetchPackageTrialAvailable,
	fetchPackageContent
}
